import { useEffect, useRef, type RefObject } from "react";
import type { SparkController, SparkState } from "../hooks/useSpark.js";
import { chargeTuning, cloudParticles, streamParticles } from "../lib/charge.js";
import { SPARK_TINT } from "../lib/spark.js";
import styles from "./ChargeCanvas.module.css";

/** Particle radius at size 1, in CSS pixels, glow included. */
const DOT_RADIUS = 4;
/** How quickly the particles fade once the spark takes off. */
const FLIGHT_FADE_MS = 400;
/** Resolution of the pre-rendered dot. */
const SPRITE_SIZE = 32;

/** One soft spark-coloured dot, drawn once and stamped for every particle. */
function createDotSprite(): HTMLCanvasElement {
  const sprite = document.createElement("canvas");
  sprite.width = SPRITE_SIZE;
  sprite.height = SPRITE_SIZE;
  const ctx = sprite.getContext("2d");
  if (!ctx) return sprite;
  const half = SPRITE_SIZE / 2;
  const color = (alpha: number): string =>
    `rgba(${SPARK_TINT.r}, ${SPARK_TINT.g}, ${SPARK_TINT.b}, ${alpha})`;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  // A small opaque core in a soft halo, like the sparks on the projection.
  gradient.addColorStop(0, color(1));
  gradient.addColorStop(0.2, color(1));
  gradient.addColorStop(0.3, color(0.45));
  gradient.addColorStop(1, color(0));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
  return sprite;
}

function draw(
  ctx: CanvasRenderingContext2D,
  sprite: HTMLCanvasElement,
  width: number,
  height: number,
  anchor: HTMLElement | null,
  state: Readonly<SparkState>,
  now: number,
  reducedMotion: boolean,
): void {
  ctx.clearRect(0, 0, width, height);
  if (!anchor || state.charge <= 0) return;

  const fade =
    state.flightStart === null ? 1 : 1 - Math.min(1, (now - state.flightStart) / FLIGHT_FADE_MS);
  if (fade <= 0) return;

  const rect = anchor.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const cloudRadius = rect.width * chargeTuning.cloudRadius;
  // The spark rests in the middle of the screen; see SparkCanvas.
  const sparkX = width / 2;
  const sparkY = height / 2;
  const streamTop = cy - cloudRadius;

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const stamp = (x: number, y: number, size: number, alpha: number): void => {
    const r = DOT_RADIUS * size;
    ctx.globalAlpha = Math.min(1, alpha * fade);
    ctx.drawImage(sprite, x - r, y - r, r * 2, r * 2);
  };
  for (const p of cloudParticles(state.charge, now, reducedMotion)) {
    stamp(cx + p.x * cloudRadius, cy + p.y * cloudRadius, p.size, p.alpha);
  }
  for (const p of streamParticles(state.charge, now, reducedMotion)) {
    stamp(
      cx + (sparkX - cx) * p.t + p.offset * cloudRadius,
      streamTop + (sparkY - streamTop) * p.t,
      p.size,
      p.alpha,
    );
  }
  ctx.restore();
}

interface ChargeCanvasProps {
  spark: SparkController;
  /** The element the particles gather around. */
  anchorRef: RefObject<HTMLElement | null>;
}

/** Full-screen canvas for the particles that gather around the hold button while charging. */
export function ChargeCanvas({ spark, anchorRef }: ChargeCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const sprite = createDotSprite();
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let width = 0;
    let height = 0;

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const unsubscribe = spark.onFrame((state, now) =>
      draw(ctx, sprite, width, height, anchorRef.current, state, now, motionQuery.matches),
    );
    return () => {
      unsubscribe();
      window.removeEventListener("resize", resize);
    };
  }, [spark, anchorRef]);

  return <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />;
}
